import { StringToNumber } from "@/@types/customTypes";
import { MatchData, OddsData } from "@/interfaces/liveGamesType";
import { api, apiPublic } from "@/services/api";
import {
  InfiniteData,
  UndefinedInitialDataInfiniteOptions,
  UseInfiniteQueryOptions,
  UseQueryOptions,
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";

interface IHistAndPredictionsData {
  time: string;
  pageSize: number;
  ligas?: RegExp["source"];
  search?: string;
}
interface IGameData extends MatchData {
  loading?: boolean;
}

interface IGameAnalysis extends MatchData {
  iframeId?: number;
  matchOdds: StringToNumber<OddsData>;
}

export const fetchPredictions = (
  data: IHistAndPredictionsData,
  options?: Partial<UndefinedInitialDataInfiniteOptions<IGameData[], unknown>>
) => {
  const response = useInfiniteQuery({
    queryKey: ["/football/getPrediction", data.time, data.ligas, data.search],
    queryFn: async (el) => {
      const res = await api.get("/football/getPrediction", {
        params: {
          ligas: data.ligas,
          from: data.time,
          to: data.time,
          page: el.pageParam,
          pageSize: data.pageSize,
          search: data.search,
        },
      });
      return res.data.data || [];
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = lastPage && lastPage.length ? allPages.length + 1 : undefined;
      return nextPage;
    },
    ...options,
  });

  return response;
};

export const fetchHistoryMatches = (
  data: IHistAndPredictionsData,
  options?: Partial<UndefinedInitialDataInfiniteOptions<IGameData[], unknown>>
) => {
  const response = useInfiniteQuery({
    queryKey: ["/football/getHistMatches", data.time, data.ligas, data.search],
    queryFn: async (el) => {
      const res = await api.get("/football/getHistMatches", {
        params: {
          ligas: data.ligas,
          from: data.time,
          to: data.time,
          page: el.pageParam,
          pageSize: data.pageSize,
          search: data.search,
        },
      });
      return res.data.data || [];
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = lastPage && lastPage.length ? allPages.length + 1 : undefined;
      return nextPage;
    },
    ...options,
  });

  return response;
};

export const fetchLiveMatches = (
  setGameAnalysis: (value: React.SetStateAction<IGameAnalysis[]>) => void,
  options?: Partial<UseQueryOptions<IGameData[]>>
) => {
  const response = useQuery({
    queryKey: ["/football/getLiveMatches"],
    queryFn: async () => {
      const games = await api.get("/football/getLiveMatches");

      const allLiveMatches = games.data?.data || [];
      setGameAnalysis((prevState) =>
        prevState.map((analysisItem) => {
          const matchingGame = allLiveMatches.find(
            (liveItem: MatchData) => liveItem.match_id === analysisItem.match_id
          );

          if (matchingGame) {
            return {
              ...matchingGame,
              iframeId: analysisItem.iframeId,
              matchOdds: analysisItem.matchOdds,
            };
          }

          return analysisItem;
        })
      );
      return allLiveMatches;
    },
    ...options,
  });

  return response;
};
